import React, { useMemo } from 'react';
import { Grid, Typography, makeStyles, createStyles } from '@material-ui/core';
import {
  BulkChangeBillingPreviewModel,
  BulkChangeBillingPreviewResponse,
} from '@lib/state/api/generated/reservations';
import {
  PreviewFilter,
  classifyRow,
  formatCurrency,
  formatSignedCurrency,
} from '../bulk-change-billing-utils';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles(theme =>
  createStyles({
    summaryGrid: {
      marginBottom: theme.spacing(2),
    },
    summaryTile: {
      textAlign: 'center',
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      cursor: 'pointer',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      '&:hover': {
        borderColor: theme.palette.primary.main,
      },
    },
    summaryTileActive: {
      borderColor: theme.palette.primary.main,
      boxShadow: `inset 0 -2px 0 ${theme.palette.primary.main}`,
    },
    summaryLabel: {
      lineHeight: 1.3,
    },
    summaryValue: {
      fontWeight: 600,
    },
  })
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export interface PreviewSummaryProps {
  preview: BulkChangeBillingPreviewResponse;
  rows: BulkChangeBillingPreviewModel[];
  activeFilter: PreviewFilter;
  onFilterChange: (filter: PreviewFilter) => void;
  /** Currently-selected reservation IDs. Tiles + Net Difference derive from this subset. */
  selectedIds: Set<string>;
}

export const PreviewSummary: React.FC<PreviewSummaryProps> = ({
  preview,
  rows,
  activeFilter,
  onFilterChange,
  selectedIds,
}) => {
  const styles = useStyles();

  const counts = useMemo(() => {
    let increases = 0;
    let decreases = 0;
    let noChange = 0;
    let blocked = 0;
    let increaseAmount = 0;
    let decreaseAmount = 0;
    // Totals per bucket (regardless of selection) for "selected/total" display.
    let totalIncreases = 0;
    let totalDecreases = 0;
    let totalNoChange = 0;
    let totalEligible = 0;
    for (const row of rows) {
      const c = classifyRow(row);
      // Blocked rows are never selectable; always count them so the Blocked tile
      // reflects the real preview (it's informational, not actionable).
      if (c === 'blocked') {
        blocked++;
        continue;
      }
      totalEligible++;
      if (c === 'increases') totalIncreases++;
      else if (c === 'decreases') totalDecreases++;
      else if (c === 'noChange') totalNoChange++;
      // Eligible rows only contribute to the actionable buckets when selected.
      if (!row.reservationId || !selectedIds.has(row.reservationId)) continue;
      const net = row.netDifference ?? 0;
      if (c === 'increases') {
        increases++;
        increaseAmount += net;
      } else if (c === 'decreases') {
        decreases++;
        decreaseAmount += net;
      } else if (c === 'noChange') {
        noChange++;
      }
    }
    return {
      increases,
      decreases,
      noChange,
      blocked,
      increaseAmount,
      decreaseAmount,
      totalIncreases,
      totalDecreases,
      totalNoChange,
      totalEligible,
    };
  }, [rows, selectedIds]);

  // Net difference is derived from the currently-selected subset so it stays in
  // sync with the actionable tiles above.
  const selectedNetDifference = counts.increaseAmount + counts.decreaseAmount;

  const tiles = useMemo<
    { key: PreviewFilter; label: string; value: string; highlight?: 'error' | 'success' }[]
  >(
    () => [
      { key: 'all', label: 'Total', value: `${selectedIds.size}/${counts.totalEligible}` },
      {
        key: 'increases',
        label: `Increases (${counts.increases}/${counts.totalIncreases})`,
        value: formatCurrency(counts.increaseAmount),
        highlight: 'success',
      },
      {
        key: 'decreases',
        label: `Decreases (${counts.decreases}/${counts.totalDecreases})`,
        value: formatCurrency(Math.abs(counts.decreaseAmount)),
        highlight: 'error',
      },
      {
        key: 'noChange',
        label: `No Change (${counts.noChange}/${counts.totalNoChange})`,
        value: '—',
      },
      {
        key: 'blocked',
        label: `Blocked (${counts.blocked})`,
        value: `${counts.blocked} reservation${counts.blocked !== 1 ? 's' : ''}`,
      },
      {
        key: 'all',
        label: 'Net Difference',
        value: formatSignedCurrency(selectedNetDifference),
        highlight:
          selectedNetDifference > 0 ? 'success' : selectedNetDifference < 0 ? 'error' : undefined,
      },
    ],
    [counts, selectedIds, selectedNetDifference]
  );

  return (
    <Grid container spacing={1} className={styles.summaryGrid}>
      {tiles.map((tile, idx) => (
        <Grid item xs={6} sm={4} md={2} key={`${tile.key}-${idx}`}>
          <div
            className={`${styles.summaryTile} ${
              activeFilter === tile.key ? styles.summaryTileActive : ''
            }`}
            onClick={() => onFilterChange(tile.key)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onFilterChange(tile.key)}
          >
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              className={styles.summaryLabel}
            >
              {tile.label}
            </Typography>
            <Typography
              variant="body2"
              className={styles.summaryValue}
              color={tile.highlight === 'error' ? 'error' : 'inherit'}
              style={tile.highlight === 'success' ? { color: '#388e3c' } : undefined}
            >
              {tile.value}
            </Typography>
          </div>
        </Grid>
      ))}
    </Grid>
  );
};
